import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getBuildingTypes } from 'features/bpList/ducks/selectors'
import { isEmpty, isNil, propOr, values } from 'ramda'
import { hasMultipleUnits, shouldCreateMultipleUnits } from 'utils/units'
import DetailsUnitSection from 'features/bpDetails/Components/units/DetailsUnitSection'
import styled, { keyframes, css } from 'styled-components'
import UnitDetails from 'features/bpDetails/Components/units/UnitDetails'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import SyncIcon from '@mui/icons-material/Sync'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import { SkeletonBlock, SkeletonLine, SkeletonRows } from 'components/Skeleton'
import { toast } from 'react-toastify'
import { downloadUnitQrStickerSheet } from 'services/qrLandingService'
import { downloadBlob } from 'utils/downloadBlob'
import {
  clearSelectedUnitRoutine,
  fetchBpUnitsRoutine,
  fetchUnitRoutine
} from 'ducks/units/actions'
import {
  selectBpUnits,
  selectSelectedUnit,
  selectSelectedUnitNotFound,
  selectUnitsFetched,
  selectUnitsLoading
} from 'ducks/units/selectors'
import Modal from 'components/atoms/Modal'
import { narrowToSavedBuildingUnits } from 'features/createBp/components/steps/blockOfFlatsReference'
import AddUnitsToBpForm from 'features/bpDetails/Components/units/AddUnitsToBpForm'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { useParams } from 'react-router'
import { redirect } from 'utils/paths'
import { isNotNilOrEmpty } from 'utils/ramda'
import OsBuildingMultiSelectMap from 'features/createBp/components/OsBuildingMultiSelectMap'
import * as bpService from 'services/BpService'
import {
  collectCandidateUprns,
  preferPopulatedList,
  selectBpMapPostcode,
  selectPostcoderFocusCandidates
} from 'utils/postcodeMapSelection'

const isWholeBuildingPlaceholderUnit = unit => {
  const unitName = String(unit?.name || '')
    .trim()
    .toLowerCase()

  return unitName.startsWith('entirety of ')
}

const normalizeUprn = value => String(value || '').trim()

const logPostcodeMap = (event, details = {}) => {
  if (process.env.NODE_ENV !== 'production') {
    window.console.info(`[OS postcode hints] ${event}`, details)
  }
}

const UK_POSTCODE_PATTERN = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i

const normalizePostcode = value =>
  String(value || '')
    .trim()
    .toUpperCase()

const extractPostcode = value => {
  const match = String(value || '').match(UK_POSTCODE_PATTERN)
  return match ? normalizePostcode(match[0]) : ''
}

const getBpPostcode = bp => {
  const directPostcode =
    bp?.postalCode ||
    bp?.postal_code ||
    bp?.postcode ||
    bp?.post_code ||
    bp?.addressData?.postcode ||
    bp?.address_data?.postcode

  if (directPostcode) {
    return normalizePostcode(directPostcode)
  }

  return extractPostcode(
    [
      bp?.address,
      bp?.fullAddress,
      bp?.full_address,
      bp?.street,
      bp?.city,
      bp?.name
    ]
      .filter(Boolean)
      .join(', ')
  )
}

const isReferenceBuildingUnit = (unit, bp) => {
  const unitUprn = normalizeUprn(unit?.uprn)
  const bpUprn = normalizeUprn(bp?.uprn)

  return Boolean(unitUprn && bpUprn && unitUprn === bpUprn)
}

const validMapCoordinates = points =>
  (points || [])
    .map(point => [Number(point.longitude), Number(point.latitude)])
    .filter(
      coordinate =>
        Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1])
    )

const candidateToids = points =>
  (points || [])
    .flatMap(point => [
      ...(Array.isArray(point?.topographicAreaToids)
        ? point.topographicAreaToids
        : []),
      point?.topographicAreaToid,
      point?.topography_layer_toid,
      point?.toid
    ])
    .map(toid => String(toid || '').trim())
    .filter(Boolean)

const candidateUnitCount = candidates =>
  (candidates || []).reduce((total, candidate) => {
    const count =
      Number(candidate?.suggestedUnitsCount) ||
      Number(candidate?.addressesCount) ||
      Number(candidate?.count) ||
      0

    return total + count
  }, 0)

const UnitsPanel = ({ redirectPath, onRequestSyncUnits }) => {
  const { id, unitId } = useParams()
  const bp = useSelector(getSelectedBp)
  const dispatch = useDispatch()
  const [collapsedSections, setCollapsedSections] = useState([])
  const buildingTypes = useSelector(getBuildingTypes)
  const selectedUnit = useSelector(selectSelectedUnit)
  const selectedUnitNotFound = useSelector(selectSelectedUnitNotFound)
  const bpUnits = useSelector(selectBpUnits)
  const unitsLoading = useSelector(selectUnitsLoading)
  const unitsFetched = useSelector(selectUnitsFetched)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [mapSelection, setMapSelection] = useState({
    toids: [],
    uprns: [],
    features: [],
    links: []
  })
  const [mapFocusRequest, setMapFocusRequest] = useState(null)
  const syncUprns = useMemo(
    () =>
      narrowToSavedBuildingUnits(bp?.selectedMapUprns || [], mapSelection.uprns),
    [bp?.selectedMapUprns, mapSelection.uprns]
  )
  const [mapHintsLoading, setMapHintsLoading] = useState(false)
  const [postcodeSyncLoading, setPostcodeSyncLoading] = useState(false)
  const hasFullAccess = bp.hasAccessToBuildingPassport
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
  const bpPostcode = selectBpMapPostcode(getBpPostcode(bp), bp?.name)
  const bpUprn = normalizeUprn(bp?.uprn)

  const isSoleUnitBuilding = useMemo(() => {
    return (
      bp.buildingTypes &&
      bp.buildingTypes.some(
        type => type.type === 'residential.dwelling.sole_unit'
      )
    )
  }, [bp])

  useEffect(() => {
    if (id) {
      dispatch(fetchBpUnitsRoutine({ bpId: id }))
    }
    setEditMode(false)
  }, [id])

  useEffect(() => {
    if (isNotNilOrEmpty(unitId)) {
      dispatch(fetchUnitRoutine({ bpId: id, unitId }))
    } else {
      dispatch(clearSelectedUnitRoutine())
    }
  }, [unitId])

  useEffect(() => {
    !isNil(selectedUnit) && dispatch(clearSelectedUnitRoutine())
  }, [])

  const toggleSectionOpen = index => {
    setCollapsedSections(prev => {
      let previous = [...prev]
      previous[index] = !previous[index]
      return previous
    })
  }

  const handleModalOpen = () => setAddModalOpen(true)
  const handleModalClose = () => setAddModalOpen(false)

  const [stickerDownloading, setStickerDownloading] = useState(false)
  const handleDownloadStickerSheet = async () => {
    setStickerDownloading(true)
    try {
      const res = await downloadUnitQrStickerSheet(id)
      const safe = (bp?.name || 'building').replace(/[^A-Za-z0-9_-]+/g, '_')
      downloadBlob(res?.data, `unit-qr-stickers-${safe}.pdf`)
    } catch (e) {
      toast.error("Couldn't generate the sticker sheet. Please try again.")
    } finally {
      setStickerDownloading(false)
    }
  }

  const sections = useMemo(() => {
    if (!isEmpty(buildingTypes)) {
      const types = propOr([], 'buildingTypes', bp)
      return types.map(type => {
        const foundType = values(buildingTypes).find(
          bpType =>
            bpType.key ===
            type.type.replace('.dwelling', '').replace('.sole_unit', '')
        )
        return {
          name: propOr('', 'label', foundType),
          multi: foundType.key ? hasMultipleUnits(foundType.key) : false,
          createMulti: foundType.key
            ? shouldCreateMultipleUnits(foundType.key)
            : false,
          value: type.value,
          buildingType: type.type
        }
      })
    }
    return []
  }, [bp, buildingTypes])

  useEffect(() => {
    if (sections.length === 0) return
    setCollapsedSections(sections.map(() => !isMobile))
  }, [sections.length, isMobile])

  const goBack = () => {
    redirect(`${redirectPath}/${id}/units`)
  }

  const isUnitRoute = useMemo(() => isNotNilOrEmpty(unitId), [unitId])
  const isUnitSelected = useMemo(
    () => isUnitRoute || !isNil(selectedUnit),
    [isUnitRoute, selectedUnit]
  )
  const realSavedUnits = useMemo(
    () =>
      (bpUnits || []).filter(
        unit =>
          unit?.uprn &&
          !isWholeBuildingPlaceholderUnit(unit) &&
          !isReferenceBuildingUnit(unit, bp)
      ),
    [bpUnits, bp]
  )
  const hasSavedUnits = realSavedUnits.length > 0
  const shouldUseMapFirst =
    !isUnitSelected &&
    hasFullAccess &&
    Boolean(bpPostcode) &&
    !hasSavedUnits &&
    (unitsFetched || !unitsLoading)
  const shouldLoadPostcodeMapSelection =
    !isUnitSelected &&
    hasFullAccess &&
    Boolean(bpPostcode) &&
    (shouldUseMapFirst || hasSavedUnits) &&
    (unitsFetched || !unitsLoading)
  const hasResolvedMapSelection = mapSelection.features.length > 0
  const mapLinkedUnitsFound = hasResolvedMapSelection
    ? mapSelection.uprns.length
    : 0
  const shouldShowMapSummary =
    hasSavedUnits &&
    Boolean(bpPostcode) &&
    (hasResolvedMapSelection || mapFocusRequest || mapHintsLoading)
  const shouldWaitForInitialMapFocus = mapHintsLoading && !mapFocusRequest

  useEffect(() => {
    if (!shouldLoadPostcodeMapSelection || !bpPostcode) {
      return undefined
    }

    let disposed = false

    const loadPostcodeFocus = async () => {
      setMapHintsLoading(true)

      let coordinates = []
      let targetUprns = []
      let targetToids = []
      let linkedUnitsFound = 0
      let referenceDetails = null
      let hintSource = 'local-uprn-postcodes'

      try {
        const { data } = await bpService.getPostcodeUprnHints({
          postcode: bpPostcode,
          uprn: bpUprn || undefined
        })
        const localPoints = data?.points || []

        logPostcodeMap('response received', {
          requestedPostcode: bpPostcode,
          requestedUprn: bpUprn || null,
          responsePostcode: data?.normalizedPostcode || data?.postcode || null,
          targetToids: data?.toids || [],
          uprnsCount: data?.uprns?.length || 0,
          pointsCount: localPoints.length,
          pointToids: candidateToids(localPoints),
          referenceUprn: data?.reference?.uprn || null
        })

        targetUprns = preferPopulatedList(
          data?.uprns,
          collectCandidateUprns(localPoints)
        )
        targetToids = preferPopulatedList(
          data?.toids,
          candidateToids(localPoints)
        )
        linkedUnitsFound = targetUprns.length
        referenceDetails = data?.reference || null
        coordinates = validMapCoordinates(localPoints)
      } catch (error) {
        logPostcodeMap('request failed', {
          requestedPostcode: bpPostcode,
          requestedUprn: bpUprn || null,
          status: error?.response?.status || null,
          response: error?.response?.data || null,
          message: error?.message || ''
        })
        coordinates = []
        targetUprns = []
        targetToids = []
      }

      if (!targetToids.length && shouldUseMapFirst) {
        logPostcodeMap('using Postcoder fallback', {
          reason: 'postcode hints returned no TOIDs',
          postcode: bpPostcode
        })
        try {
          const { data } = await bpService.lookupBuildingReference({
            postcode: bpPostcode,
            list: true
          })
          const candidates = data?.candidates || []
          const focusCandidates = selectPostcoderFocusCandidates(
            candidates,
            bpUprn
          )
          const candidateCoordinates = validMapCoordinates(focusCandidates)
          const candidateTargetUprns = collectCandidateUprns(candidates)
          const candidateTargetToids = candidateToids(focusCandidates)

          if (candidateCoordinates.length) {
            coordinates = candidateCoordinates
          }
          if (candidateTargetUprns.length) {
            targetUprns = candidateTargetUprns
          }
          targetToids = candidateTargetToids
          linkedUnitsFound =
            candidateUnitCount(candidates) || targetUprns.length
          referenceDetails = focusCandidates[0] || referenceDetails
          hintSource = targetUprns.length ? 'postcoder-fallback' : hintSource
          logPostcodeMap('Postcoder fallback received', {
            candidatesCount: candidates.length,
            focusCandidatesCount: focusCandidates.length,
            referenceUprn: bpUprn || null,
            targetToids,
            targetUprnsCount: targetUprns.length,
            coordinatesCount: coordinates.length
          })
        } catch (error) {
          logPostcodeMap('Postcoder fallback failed', {
            status: error?.response?.status || null,
            response: error?.response?.data || null,
            message: error?.message || ''
          })
          // Keep the OS map usable even if the paid fallback fails.
        }
      }

      if (disposed) {
        return
      }

      const nextFocusRequest = {
        id: Date.now(),
        query: bpPostcode,
        totalLinkedUnitsFound: linkedUnitsFound,
        targetUprns: Array.from(new Set(targetUprns)),
        targetToids,
        coordinates,
        referenceDetails,
        hintSource
      }

      logPostcodeMap('focus request created', {
        query: nextFocusRequest.query,
        hintSource: nextFocusRequest.hintSource,
        targetToids: nextFocusRequest.targetToids,
        targetUprnsCount: nextFocusRequest.targetUprns.length,
        coordinatesCount: nextFocusRequest.coordinates.length
      })
      setMapFocusRequest(nextFocusRequest)

      setMapHintsLoading(false)
    }

    loadPostcodeFocus().catch(() => {
      if (!disposed) {
        setMapHintsLoading(false)
        if (disposed) {
          return
        }

        setMapFocusRequest({
          query: bpPostcode,
          targetUprns: [],
          targetToids: [],
          coordinates: [],
          totalLinkedUnitsFound: 0,
          referenceDetails: null,
          hintSource: 'postcode'
        })
      }
    })

    return () => {
      disposed = true
    }
  }, [shouldLoadPostcodeMapSelection, shouldUseMapFirst, bpPostcode, bpUprn])

  const handleMapSelectionChange = selection => {
    setMapSelection({
      toids: selection?.toids || [],
      uprns: selection?.uprns || [],
      features: selection?.features || [],
      links: selection?.links || []
    })
  }

  const handleSyncSelectedMapUnits = async () => {
    if (!onRequestSyncUnits || postcodeSyncLoading) {
      return
    }

    const selectedUprns = syncUprns || []

    if (selectedUprns.length) {
      onRequestSyncUnits?.({ selectedUprns })
      return
    }

    if (shouldUseMapFirst) {
      toast.info('Select a building outline on the OS map first.')
      return
    }

    if (bpPostcode) {
      setPostcodeSyncLoading(true)

      try {
        const { data } = await bpService.getPostcodeUprns({
          postcode: bpPostcode
        })
        const postcodeUprns = Array.from(
          new Set(
            (data?.uprns || [])
              .map(uprn => String(uprn || '').trim())
              .filter(Boolean)
          )
        )

        if (postcodeUprns.length) {
          onRequestSyncUnits?.({ selectedUprns: postcodeUprns })
          return
        }
      } catch (error) {
        toast.error('Could not load local UPRNs for this postcode.')
      } finally {
        setPostcodeSyncLoading(false)
      }
    }

    onRequestSyncUnits?.()
  }

  return (
    <PanelContainer>
      <HeaderSection isUnitSelected={isUnitSelected}>
        <HeaderContent>
          {isUnitSelected ? (
            <BackButton onClick={goBack}>
              <BackIconWrapper>
                <ArrowBackIcon />
              </BackIconWrapper>
              <BackText>Back to Units</BackText>
            </BackButton>
          ) : (
            <PageTitle>Unit Details</PageTitle>
          )}

          {!isUnitSelected && (
            <HeaderActions>
              <SyncUnitsButton
                type='button'
                data-tour-id='units.sync-button'
                onClick={handleSyncSelectedMapUnits}
                disabled={
                  shouldUseMapFirst
                    ? !mapSelection.uprns.length
                    : !onRequestSyncUnits || postcodeSyncLoading
                }
              >
                <SyncIconStyled />
                <span className='button-text'>
                  {postcodeSyncLoading ? 'Loading UPRNs...' : 'Sync Units'}
                </span>
              </SyncUnitsButton>
              {hasFullAccess && (
                <SyncUnitsButton
                  type='button'
                  onClick={handleDownloadStickerSheet}
                  disabled={stickerDownloading}
                  title='Download a printable A4 sheet of QR stickers — one per unit'
                >
                  <QrCode2OutlinedIcon style={{ fontSize: 18 }} />
                  <span className='button-text'>
                    {stickerDownloading ? 'Preparing…' : 'Print QR stickers'}
                  </span>
                </SyncUnitsButton>
              )}
              {hasFullAccess && !isSoleUnitBuilding && (
                <AddNewButton onClick={handleModalOpen}>
                  <AddIconStyled />
                  <span className='button-text'>Add Units</span>
                </AddNewButton>
              )}
            </HeaderActions>
          )}
        </HeaderContent>
      </HeaderSection>

      <ContentArea>
        {isUnitSelected ? (
          <UnitDetailsWrapper>
            {selectedUnit ? (
              <UnitDetails unit={selectedUnit} />
            ) : selectedUnitNotFound ? (
              <UnitRemovedWrapper>
                <UnitRemovedTitle>This unit is no longer available</UnitRemovedTitle>
                <UnitRemovedText>
                  It may have been removed from this Building Passport, or you may no longer have access to it.
                </UnitRemovedText>
                <UnitRemovedButton
                  type='button'
                  onClick={() => redirect(`${redirectPath}/${id}/units`)}
                >
                  Back to units
                </UnitRemovedButton>
              </UnitRemovedWrapper>
            ) : (
              <UnitLoadingWrapper aria-busy='true' aria-label='Loading unit'>
                <SkeletonLine $w='38%' $h={22} />
                <SkeletonBlock $h={96} />
                <SkeletonBlock $h={160} />
              </UnitLoadingWrapper>
            )}
          </UnitDetailsWrapper>
        ) : unitsLoading && !unitsFetched ? (
          <UnitLoadingWrapper aria-busy='true' aria-label='Loading units'>
            <SkeletonBlock $h={44} $r='10px' />
            <SkeletonRows rows={6} widths={['30%', '18%', '10%', '10%', '16%']} />
          </UnitLoadingWrapper>
        ) : shouldUseMapFirst ? (
          <MapFirstPanel data-testid='units-os-map-first-panel'>
            <MapFirstHeader>
              <MapFirstTitle>Sync units from OS map</MapFirstTitle>
              <MapFirstMeta>
                {mapHintsLoading
                  ? 'Loading postcode building data...'
                  : hasResolvedMapSelection && syncUprns.length
                    ? `${syncUprns.length} linked UPRNs selected`
                    : mapFocusRequest?.targetUprns?.length ||
                        mapFocusRequest?.targetToids?.length
                      ? 'Auto-selecting buildings from postcode...'
                      : 'Select the building outline to resolve linked UPRNs'}
              </MapFirstMeta>
            </MapFirstHeader>
            {shouldWaitForInitialMapFocus ? (
              <MapFocusPlaceholder aria-busy='true'>
                <MapSkeletonFill />
                <MapLoadingCaption>Loading postcode building data…</MapLoadingCaption>
              </MapFocusPlaceholder>
            ) : (
              <OsBuildingMultiSelectMap
                selectedToids={mapSelection.toids}
                selectedUprns={mapSelection.uprns}
                selectedFeatures={mapSelection.features}
                focusRequest={mapFocusRequest}
                totalLinkedUnitsFound={mapLinkedUnitsFound}
                onSelectionChange={handleMapSelectionChange}
                postcodeSearchLoading={mapHintsLoading}
                hideHeaderTitle
              />
            )}
          </MapFirstPanel>
        ) : (
          <SectionsGrid>
            {shouldShowMapSummary && (
              <MapSummaryHeader>
                <MapSummaryCopy>
                  <MapSummaryTitle>OS building selection</MapSummaryTitle>
                  <MapSummaryMeta>
                    {mapHintsLoading
                      ? 'Loading postcode building data...'
                      : hasResolvedMapSelection
                        ? `${mapSelection.toids.length} building${
                            mapSelection.toids.length === 1 ? '' : 's'
                          } selected - ${syncUprns.length} linked UPRN${
                            syncUprns.length === 1 ? '' : 's'
                          }`
                        : 'Auto-selecting buildings from postcode...'}
                  </MapSummaryMeta>
                </MapSummaryCopy>
                {shouldWaitForInitialMapFocus ? (
                  <MapFocusPlaceholder $compact aria-busy='true'>
                    <MapSkeletonFill />
                    <MapLoadingCaption>Loading postcode building data…</MapLoadingCaption>
                  </MapFocusPlaceholder>
                ) : (
                  <OsBuildingMultiSelectMap
                    selectedToids={mapSelection.toids}
                    selectedUprns={mapSelection.uprns}
                    selectedFeatures={mapSelection.features}
                    focusRequest={mapFocusRequest}
                    totalLinkedUnitsFound={
                      mapLinkedUnitsFound ||
                      mapFocusRequest?.totalLinkedUnitsFound ||
                      0
                    }
                    onSelectionChange={handleMapSelectionChange}
                    postcodeSearchLoading={mapHintsLoading}
                    compact
                  />
                )}
              </MapSummaryHeader>
            )}
            {sections &&
              sections.map((section, index) => (
                <SectionCard key={`section-${index}`}>
                  <DetailsUnitSection
                    open={collapsedSections[index]}
                    section={section}
                    bp={bp}
                    sectionsCount={sections.length}
                    toggleOpen={() => toggleSectionOpen(index)}
                    redirectPath={`${redirectPath}/${id}/units`}
                    onRequestAddUnits={handleModalOpen}
                    editMode={editMode}
                    onToggleEditMode={() => setEditMode(prev => !prev)}
                  />
                </SectionCard>
              ))}
          </SectionsGrid>
        )}
      </ContentArea>

      <Modal
        onClose={handleModalClose}
        onCancel={handleModalClose}
        open={addModalOpen}
        title='Add new units'
      >
        <AddUnitsToBpForm bp={bp} closeModal={handleModalClose} />
      </Modal>
    </PanelContainer>
  )
}

export default UnitsPanel

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '1024px'
}

const colors = {
  darkNavy: '#0c132b',
  teal: '#36becd',
  lightBg: '#fcfbfe',
  lightBg2: '#f6f7fe',
  lightBg3: '#eef2fa',
  lightBg4: '#e8eaf5',
  lightBg5: '#cdd4ea',
  lightTeal: '#bbeaf0'
}

const MapFirstPanel = styled.section`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  max-width: 1180px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(12, 18, 41, 0.08);
  border-radius: 8px;
  padding: 18px;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 12px;
    border-right: 0;
    border-left: 0;
    border-radius: 0;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px 8px;
  }
`

const MapFirstHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
  }
`

const MapFirstTitle = styled.h3`
  margin: 0;
  color: ${colors.darkNavy};
  font-size: 18px;
  font-weight: 700;
`

const MapFirstMeta = styled.div`
  color: ${({ theme }) => theme.colors.grey[900]};
  font-size: 13px;
  line-height: 1.4;
`

const MapSummaryHeader = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(12, 18, 41, 0.08);
  border-radius: 8px;
  padding: 14px;
  animation: ${fadeIn} 0.3s ease-out;
`

const MapSummaryCopy = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 10px;

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
    gap: 4px;
  }
`

const MapSummaryTitle = styled.h3`
  margin: 0;
  color: ${colors.darkNavy};
  font-size: 16px;
  font-weight: 700;
`

const MapSummaryMeta = styled.div`
  color: ${({ theme }) => theme.colors.grey[900]};
  font-size: 12px;
  font-weight: 700;
`

const MapFocusPlaceholder = styled.div`
  position: relative;
  overflow: hidden;
  height: ${({ $compact }) =>
    $compact ? '220px' : 'clamp(520px, 58vh, 760px)'};
  min-height: ${({ $compact }) => ($compact ? '220px' : '520px')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 10px 0 18px;
  border: 1px solid rgba(12, 18, 41, 0.12);
  border-radius: 8px;
  background: #f6f8fb;
  color: ${colors.darkNavy};
  font-size: 13px;
  font-weight: 700;

  ${({ $compact }) =>
    $compact &&
    `
      margin: 0;
    `}

  @media (max-width: ${breakpoints.tablet}) {
    height: ${({ $compact }) => ($compact ? '220px' : 'min(52vh, 420px)')};
    height: ${({ $compact }) => ($compact ? '220px' : 'min(52dvh, 420px)')};
    min-height: ${({ $compact }) => ($compact ? '220px' : '300px')};
  }

  @media (max-width: ${breakpoints.mobile}) {
    height: ${({ $compact }) => ($compact ? '210px' : 'min(48vh, 360px)')};
    height: ${({ $compact }) => ($compact ? '210px' : 'min(48dvh, 360px)')};
    min-height: ${({ $compact }) => ($compact ? '210px' : '280px')};
  }
`

const PanelContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    ${colors.lightBg} 0%,
    ${colors.lightBg2} 100%
  );
  padding-bottom: env(safe-area-inset-bottom, 20px);
  display: flex;
  flex-direction: column;

  @media (max-width: ${breakpoints.tablet}) {
    padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 60px);
  }
`

const HeaderSection = styled.header`
  position: sticky;
  top: 0;
  background: rgba(252, 251, 254, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 0 24px;
  transition: all 0.2s ease;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 0 16px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0 8px;
  }
`

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: ${breakpoints.tablet}) {
    height: 60px;
  }
`

const PageTitle = styled.h1`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${colors.darkNavy};
  letter-spacing: -0.5px;
  margin: 0;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  animation: ${fadeIn} 0.4s ease-out;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 20px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 18px;
  }
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 12px 8px 8px;
  margin-left: -8px;
  border-radius: 12px;
  transition: all 0.2s ease;
  animation: ${slideIn} 0.3s ease-out;
  flex-shrink: 0;

  &:hover {
    background: ${colors.lightBg3};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 4px;
    margin-left: -4px;
    gap: 0;
  }
`

const BackIconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 5px;
  background: ${colors.teal};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(54, 190, 205, 0.3);
  flex-shrink: 0;

  svg {
    color: white;
    font-size: 20px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 32px;
    height: 32px;
    border-radius: 8px;

    svg {
      font-size: 18px;
    }
  }
`

const BackText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${colors.darkNavy};

  @media (max-width: ${breakpoints.mobile}) {
    display: none; /* Hide text on very small screens to save header space */
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.tablet}) {
    gap: 8px;
  }
`

const SyncUnitsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #fff;
  border: 1px solid ${colors.teal};
  border-radius: 5px;
  color: ${colors.teal};
  cursor: pointer;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out 0.05s both;
  flex-shrink: 0;

  .button-text {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: ${colors.teal};
    white-space: nowrap;
  }

  &:hover:not(:disabled) {
    background: #ecfeff;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(54, 190, 205, 0.18);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 0;
    width: 36px;
    height: 36px;
    justify-content: center;
    border-radius: 5px;

    .button-text {
      display: none;
    }
  }
`

const AddNewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: ${colors.teal};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 2px 10px rgba(54, 190, 205, 0.25);
  animation: ${fadeIn} 0.4s ease-out 0.1s both;
  flex-shrink: 0;

  .button-text {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(54, 190, 205, 0.35);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 0;
    width: 36px;
    height: 36px;
    justify-content: center;
    border-radius: 5px;

    .button-text {
      display: none;
    }
  }
`

const AddIconStyled = styled(AddIcon)`
  color: white;
  font-size: 20px !important;
`

const SyncIconStyled = styled(SyncIcon)`
  color: ${colors.teal};
  font-size: 20px !important;
`

const ContentArea = styled.main`
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 0;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
  }
`

const SectionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: ${breakpoints.tablet}) {
    gap: 16px;
  }
`

const SectionCard = styled.div`
  position: relative;
  z-index: 1;
  background: white;
  padding: 24px;
  transition: all 0.3s ease;
  overflow: hidden;

  @media (max-width: ${breakpoints.tablet}) {
    padding: 16px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 12px;
  }
`

const UnitDetailsWrapper = styled.div`
  background: white;
  //border-radius: 12px;
  padding: 24px;
  //box-shadow: 0 1px 3px rgba(12, 19, 43, 0.04), 0 4px 12px rgba(12, 19, 43, 0.03);
  //border: 1px solid ${colors.lightBg4};
  animation: ${fadeIn} 0.4s ease-out;
  width: 100%;
  //box-sizing: border-box;
  overflow-x: hidden; /* Prevents internal tables from breaking layout */

  @media (max-width: ${breakpoints.tablet}) {
    padding: 2px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 2px;
    border-radius: 10px;
  }
`

const UnitLoadingWrapper = styled.div`
  min-height: 280px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 8px 0;
`

const MapSkeletonFill = styled(SkeletonBlock)`
  position: absolute;
  inset: 0;
  width: auto;
  height: auto;
  border-radius: 8px;
`

const MapLoadingCaption = styled.span`
  position: relative;
`

const UnitRemovedWrapper = styled.div`
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  padding: 32px 20px;
`

const UnitRemovedTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0c1229;
`

const UnitRemovedText = styled.p`
  margin: 0;
  max-width: 420px;
  font-size: 15px;
  line-height: 1.5;
  color: #5f5f6a;
`

const UnitRemovedButton = styled.button`
  margin-top: 4px;
  min-height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #2aa6b3; }
`

const ModalContent = styled.div`
  padding: 8px 0;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
  }
`
